import { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Stack, Box, List, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { BottomCartBar } from '../appBars/cartBar/bottomCartBar';
import { CustomerInfo, ServiceItem } from 'orderme-api-integration-client';
import { ServiceClient } from '../../helpers/client';
import { FilteredItem } from './filteredItem';
import { SearchBar } from './searchBar';
import { Linear } from '../progressBars/linear';
import latinize from 'latinize';
import { NavBar } from '../appBars/navBar/navBar';
import { useTranslation } from 'react-i18next';
import { appStyleMode } from '../../helpers/clientConfigs';
import { AppStyle } from '../../helpers/appStyle';
import { StickyCartBar } from '../appBars/cartBar/stickyCartBar';
import { BreadcrumbsBar } from '../breadcrumbs/breadcrumbsBar';
import { useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import DetergentsIcon from './detergentsIcon';
import LocalLaundryServiceOutlinedIcon from '@mui/icons-material/LocalLaundryServiceOutlined';
import { SearchRoute } from '../../routes/allRoutes';
import SewingIcon from './sewingIcon';
import { isLaundryService, isSewingService } from '../../helpers/servicesHelper';

enum ServiceType {
  Laundry,
  PhysicalItem,
  Sewing,
}

type PreparedService = {
  item: ServiceItem;
  normalizedName: string;
};

export function AllServices() {
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [search, setSearch] = useState('');
  const servicesClient: ServiceClient = useMemo(() => new ServiceClient(), []);
  const customerInfo: CustomerInfo = useAppSelector((state: RootState) => state.customerState.customerInfo);
  const { t } = useTranslation();
  const [allServiceTypes, setAllServiceTypes] = useState<ServiceType[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const deferredSearch = useDeferredValue(search);

  const loadAllServices = useCallback(() => {
    servicesClient
      .getServices(customerInfo.id)
      .then((response) => {
        distinctServices(response);
      })
      .catch((err) => console.log(err));
  }, [servicesClient, customerInfo]);

  const isServiceTypeSelected = useCallback(
    (service: ServiceItem) => {
      if (service.isItem) {
        return serviceTypes.includes(ServiceType.PhysicalItem);
      }

      if (isSewingService(service)) {
        return serviceTypes.includes(ServiceType.Sewing);
      }

      if (isLaundryService(service)) {
        return serviceTypes.includes(ServiceType.Laundry);
      }

      return false;
    },
    [serviceTypes]
  );

  const preparedServices = useMemo<PreparedService[]>(() => {
    return services.map((service) => ({
      item: service,
      normalizedName: latinize(service.name).toLowerCase(),
    }));
  }, [services]);

  const filteredServices = useMemo(() => {
    if (serviceTypes.length === 0) {
      return [];
    }

    const term = latinize(deferredSearch).toLowerCase();

    return preparedServices
      .filter(({ item, normalizedName }) => normalizedName.includes(term) && isServiceTypeSelected(item))
      .sort((a, b) => {
        const isPhysicalItemA = a.item.isItem;
        const isPhysicalItemB = b.item.isItem;

        if (isPhysicalItemA && !isPhysicalItemB) {
          return -1;
        }

        if (!isPhysicalItemA && isPhysicalItemB) {
          return 1;
        }

        return a.item.name.localeCompare(b.item.name);
      })
      .map(({ item }) => item);
  }, [deferredSearch, isServiceTypeSelected, preparedServices, serviceTypes]);

  useEffect(() => {
    if (allServiceTypes.length > 0) {
      setServiceTypes(allServiceTypes);
    } else {
      setServiceTypes([]);
    }
  }, [allServiceTypes]);

  useEffect(() => {
    loadAllServices();
  }, [loadAllServices]);

  const distinctServices = (services: ServiceItem[]) => {
    const seenServices = new Set<string>();
    const uniqueServices = services.filter((item) => {
      const key = `${item.name}|${item.price}|${item.prices?.length ?? 0}`;

      if (seenServices.has(key)) {
        return false;
      }

      seenServices.add(key);
      return true;
    });

    if (uniqueServices.length > 0) {
      const existingServiceTypes: ServiceType[] = [];
      if (uniqueServices.some((service) => isSewingService(service))) {
        existingServiceTypes.push(ServiceType.Sewing);
      }
      if (uniqueServices.some((service) => isLaundryService(service))) {
        existingServiceTypes.push(ServiceType.Laundry);
      }
      if (uniqueServices.some((service) => service.isItem)) {
        existingServiceTypes.push(ServiceType.PhysicalItem);
      }
      setAllServiceTypes(existingServiceTypes);
      setServices(uniqueServices);
    }
  };

  const containerStyle = {
    maxWidth: '1000px',
    minWidth: '300px',
    backgroundColor: 'white',
    color: 'black',
    borderRadius: '15px',
    width: '100%',
  } as const;

  const mobileContainerStyle = {
    overflow: 'visible',
  } as const;

  const handleServiceTypeChange = (_event: React.MouseEvent<HTMLElement>, newServiceTypes: ServiceType[]) => {
    setServiceTypes(newServiceTypes);
  };

  return (
    <Stack direction="column" alignItems="center">
      <NavBar barTitle={t('services:allServices')} />
      <BreadcrumbsBar paths={[{ path: SearchRoute }]} />
      {services.length === 0 && <Linear />}
      {services.length > 0 && (
        <Stack
          sx={appStyleMode === AppStyle.Mobile ? mobileContainerStyle : containerStyle}
          direction="column"
          justifyContent="center"
        >
          <SearchBar setSearch={setSearch} searchValue={search} />
          {allServiceTypes.length > 1 && (
            <ToggleButtonGroup
              value={serviceTypes}
              onChange={handleServiceTypeChange}
              sx={{ justifyContent: 'center' }}
            >
              {allServiceTypes.includes(ServiceType.PhysicalItem) && (
                <ToggleButton sx={{ minWidth: '150px' }} value={ServiceType.PhysicalItem}>
                  <DetergentsIcon
                    sx={{
                      color: 'secondary.main',
                      height: '40px',
                      width: '40px',
                      p: '2px',
                      mr: '10px',
                    }}
                  />
                  {t('services:products')}
                </ToggleButton>
              )}
              {allServiceTypes.includes(ServiceType.Laundry) && (
                <ToggleButton sx={{ minWidth: '150px' }} value={ServiceType.Laundry}>
                  <LocalLaundryServiceOutlinedIcon sx={{ height: '50px', width: '50px', color: 'secondary.main' }} />
                  {t('services:services')}
                </ToggleButton>
              )}
              {allServiceTypes.includes(ServiceType.Sewing) && (
                <ToggleButton sx={{ minWidth: '150px' }} value={ServiceType.Sewing}>
                  <SewingIcon
                    sx={{
                      color: 'secondary.main',
                      height: '40px',
                      width: '40px',
                      p: '2px',
                      mr: '10px',
                    }}
                  />
                  {t('services:services')}
                </ToggleButton>
              )}
            </ToggleButtonGroup>
          )}
          <Box>
            <List sx={{ overflow: 'auto' }} className={appStyleMode === AppStyle.Mobile ? 'all-services-list' : null}>
              {filteredServices.map((service) => {
                return <FilteredItem key={service.id} service={service} />;
              })}
            </List>
          </Box>
          {appStyleMode === AppStyle.Mobile ? <BottomCartBar /> : <StickyCartBar />}
        </Stack>
      )}
    </Stack>
  );
}
